import axios from "axios";
import { coldLeadsAction } from "./coldLeadsAction";
import { showSuccessSnackbar } from "../../snackBar/snackBar.action";

const getAdminURL = (state) => state.environnment.environmentLists.adminBaseURL;

/** Safe string for created_by query (handles ObjectId-like objects from selects). */
function normalizeCreatedByQueryValue(value) {
  if (value == null || value === "") return "";
  if (typeof value === "object" && value !== null && value._id != null) {
    return String(value._id).trim();
  }
  const s = String(value).trim();
  if (s === "undefined" || s === "null") return "";
  return s;
}

export const getAllColdLeadsAsync = (searchBy, searchText, audioType = "all", batchFilter = "", fromDateFilter = "", toDateFilter = "", leadType = "all", createdByFilter = "", sortColumn = "createdOn", sortDirection = "desc", callOutcomeStatus = "") => {
  return async (dispatch, getState) => {
    try {
      const adminBaseURL = getAdminURL(getState());
      const { skip, limit } = getState().coldLeads;
      const { user } = getState().auth;
      dispatch(coldLeadsAction.getAllColdLeadsStart());
      
      // Build query parameters
      // Use searchBy as column if provided, otherwise use sortColumn (default: createdOn)
      const column = searchBy || sortColumn;
      // Use sortDirection (default: desc)
      const dir = sortDirection || "desc";
      
      let url = `${process.env.REACT_APP_HOST}${adminBaseURL}/coldLeadsCalling/getAll?skip=${skip}&limit=${limit}&column=${column}&dir=${dir}&search=${encodeURIComponent(searchText || "")}&audio_type=${audioType}`;
      
      // Add batch filter if provided
      if (batchFilter) {
        url += `&batch_name=${encodeURIComponent(batchFilter)}`;
      }
      
      // Add from date filter if provided (format: DD/MM/YYYY)
      if (fromDateFilter) {
        url += `&from_date=${encodeURIComponent(fromDateFilter)}`;
      }

      // Add to date filter if provided (format: DD/MM/YYYY)
      if (toDateFilter) {
        url += `&to_date=${encodeURIComponent(toDateFilter)}`;
      }
      
      // Add lead type filter if provided and not "all"
      if (leadType && leadType !== "all") {
        url += `&lead_type=${encodeURIComponent(leadType)}`;
      }
      
      // created_by: sales / restricted users → always their own id.
      // Super admin + himanshu: explicit "all" when no user chosen so listing includes every creator.
      let createdByParamValue = normalizeCreatedByQueryValue(createdByFilter);
      // eslint-disable-next-line eqeqeq
      const isElevated =
        user &&
        (user.roleId == 1 ||
          String(user.email || "")
            .trim()
            .toLowerCase() === "himanshu.soni@webcluesinfotech.com");
      // eslint-disable-next-line eqeqeq
      if (user && user._id && user.roleId != 1 && user.email != "himanshu.soni@webcluesinfotech.com") {
        createdByParamValue = normalizeCreatedByQueryValue(user._id);
      } else if (isElevated && !createdByParamValue) {
        createdByParamValue = "all";
      }
      if (createdByParamValue) {
        url += `&created_by=${encodeURIComponent(createdByParamValue)}`;
      }
      
      // Add call outcome status filter if provided
      if (callOutcomeStatus) {
        url += `&call_outcome_status=${encodeURIComponent(callOutcomeStatus)}`;
      }
      
      const { data } = await axios({
        method: "GET",
        url,
        headers: {
          "Content-Type": "application/json",
        },
      });
      if (data?.code === 200) {
        dispatch(coldLeadsAction.getAllColdLeadsSuccess(data.data));
      } else {
        dispatch(coldLeadsAction.getAllColdLeadsError());
        dispatch(
          showSuccessSnackbar(
            "error",
            data?.message || "Failed to fetch cold leads",
            3000
          )
        );
      }
    } catch (error) {
      console.error("Failed to fetch cold leads", error);
      dispatch(coldLeadsAction.getAllColdLeadsError());
      dispatch(showSuccessSnackbar("error", "Error while fetching cold leads", 3000));
    }
  };
};

// View single lead - Backend handles permission check:
// Super Admin (roleId == 1): Can view any lead
// Sales Team: Can only view their own leads (created_by = req._id)
export const getColdLeadAsync = (id) => {
  return async (dispatch, getState) => {
    try {
      const adminBaseURL = getAdminURL(getState());
      dispatch(coldLeadsAction.getColdLeadStart());
      const { data } = await axios({
        method: "GET",
        url: `${process.env.REACT_APP_HOST}${adminBaseURL}/coldLeadsCalling/getCallDetails/${id}`,
        headers: {
          "Content-Type": "application/json",
        },
      });
      if (data?.code === 200) {
        const payload = data.data || {};
        // Extract lead from the response structure: { lead: {...}, call_ai: [...] }
        const leadData = payload.lead || payload;
        dispatch(coldLeadsAction.getColdLeadSuccess(leadData));
      } else {
        dispatch(coldLeadsAction.getColdLeadError());
        dispatch(
          showSuccessSnackbar(
            "error",
            data?.message || "Failed to fetch cold lead",
            3000
          )
        );
      }
    } catch (error) {
      console.error("Failed to fetch cold lead", error);
      dispatch(coldLeadsAction.getColdLeadError());
      dispatch(showSuccessSnackbar("error", "Error while fetching cold lead", 3000));
    }
  };
};

// Delete single lead - Backend handles permission check:
// Super Admin (roleId == 1): Can delete any lead
// Sales Team: Can only delete their own leads (created_by = req._id)
export const deleteColdLeadAsync = (id) => {
  return async (dispatch, getState) => {
    try {
      const adminBaseURL = getAdminURL(getState());
      dispatch(coldLeadsAction.getColdLeadStart());
      const { data } = await axios({
        method: "POST",
        url: `${process.env.REACT_APP_HOST}${adminBaseURL}/coldLeadsCalling/removeMultiColdLeadsCalling`,
        headers: {
          "Content-Type": "application/json",
        },
        data: {
          data: [id],
        },
      });
      if (data?.code === 200) {
        dispatch(
          showSuccessSnackbar("success", data?.message || "Lead deleted successfully", 3000)
        );
        dispatch(coldLeadsAction.getColdLeadSuccess({}));
      } else {
        dispatch(coldLeadsAction.getColdLeadError());
        dispatch(
          showSuccessSnackbar(
            "error",
            data?.message || "Failed to delete lead",
            3000
          )
        );
      }
    } catch (error) {
      console.error("Failed to delete cold lead", error);
      dispatch(coldLeadsAction.getColdLeadError());
      dispatch(showSuccessSnackbar("error", "Error while deleting lead", 3000));
    }
  };
};

// Delete multiple leads - Backend handles permission check:
// Super Admin (roleId == 1): Can delete any leads
// Sales Team: Can only delete their own leads (created_by = req._id)
export const deleteSelectedColdLeadsAsync = (ids) => {
  return async (dispatch, getState) => {
    try {
      const adminBaseURL = getAdminURL(getState());
      dispatch(coldLeadsAction.getColdLeadStart());
      const { data } = await axios({
        method: "POST",
        url: `${process.env.REACT_APP_HOST}${adminBaseURL}/coldLeadsCalling/deletedSelectedColdLeadsCalling`,
        headers: {
          "Content-Type": "application/json",
        },
        data: {
          ids,
        },
      });
      if (data?.code === 200) {
        dispatch(
          showSuccessSnackbar(
            "success",
            data?.message || "Selected leads deleted successfully",
            3000
          )
        );
        dispatch(coldLeadsAction.getColdLeadSuccess({}));
      } else {
        dispatch(coldLeadsAction.getColdLeadError());
        dispatch(
          showSuccessSnackbar(
            "error",
            data?.message || "Failed to delete selected leads",
            3000
          )
        );
      }
    } catch (error) {
      console.error("Failed to delete selected cold leads", error);
      dispatch(coldLeadsAction.getColdLeadError());
      dispatch(showSuccessSnackbar("error", "Error while deleting selected leads", 3000));
    }
  };
};

export const importColdLeadsAsync = (file) => {
  return async (dispatch, getState) => {
    if (!file) {
      dispatch(showSuccessSnackbar("error", "Please select a file to import.", 3000));
      return { success: false, error: "No file selected" };
    }

    try {
      const adminBaseURL = getAdminURL(getState());
      const formData = new FormData();
      formData.append("file", file);

      const { data } = await axios({
        method: "POST",
        url: `${process.env.REACT_APP_HOST}${adminBaseURL}/coldLeadsCalling/import`,
        headers: {
          "Content-Type": "multipart/form-data",
        },
        data: formData,
      });

      if (data?.code === 200 && data?.status === true) {
        const importData = data.data || {};
        //const message = `${data.message || "Cold leads imported successfully."} Processed: ${importData.processed || 0}, Inserted: ${importData.inserted || 0}, Updated: ${importData.updated || 0}, Skipped: ${importData.skipped || 0}. AI Calls Initiated: ${importData.aiCalls?.initiated || 0}, Failed: ${importData.aiCalls?.failed || 0}, Skipped: ${importData.aiCalls?.skipped || 0}.`;
        const message = `${data.message || "Cold leads imported successfully."}`;
        dispatch(showSuccessSnackbar("success", message, 5000));
        return { success: true, data: importData };
      } else {
        dispatch(
          showSuccessSnackbar(
            "error",
            data?.message || "Failed to import cold leads",
            3000
          )
        );
        return { success: false, error: data?.message || "Failed to import cold leads" };
      }
    } catch (error) {
      console.error("Failed to import cold leads", error);
      const errorMessage =
        error?.response?.data?.message ||
        error?.message ||
        "Error while importing cold leads";
      dispatch(showSuccessSnackbar("error", errorMessage, 3000));
      return { success: false, error: errorMessage };
    }
  };
};

// Initiate call for a lead - Backend handles permission check:
// Super Admin (roleId == 1): Can call any lead
// Sales Team: Can only call their own leads (created_by = req._id)
export const startColdLeadCallAsync = (leadId) => {
  return async (dispatch, getState) => {
    if (!leadId) {
      dispatch(showSuccessSnackbar("error", "Lead ID is required to start a call.", 3000));
      return { success: false, error: "Lead ID is required" };
    }

    try {
      const adminBaseURL = getAdminURL(getState());
      const { data } = await axios({
        method: "POST",
        url: `${process.env.REACT_APP_HOST}${adminBaseURL}/coldLeadsCalling/initiateCall/${leadId}`,
        headers: {
          "Content-Type": "application/json",
        },
      });

      if (data?.code === 200 && data?.status === true) {
        dispatch(
          showSuccessSnackbar(
            "success",
            data?.message || "Call initiated successfully",
            3000
          )
        );
        return { success: true, data: data.data };
      } else {
        dispatch(
          showSuccessSnackbar(
            "error",
            data?.message || "Failed to initiate call",
            3000
          )
        );
        return { success: false, error: data?.message || "Failed to initiate call" };
      }
    } catch (error) {
      console.error("Failed to initiate call", error);
      const errorMessage =
        error?.response?.data?.message ||
        error?.message ||
        "Error while initiating call";
      dispatch(showSuccessSnackbar("error", errorMessage, 3000));
      return { success: false, error: errorMessage };
    }
  };
};

// Bulk call multiple leads - Backend handles permission check:
// Super Admin (roleId == 1): Can call any leads
// Sales Team: Can only call their own leads (created_by = req._id)
export const bulkCallColdLeadsAsync = (leadIds) => {
  return async (dispatch, getState) => {
    if (!leadIds || leadIds.length === 0) {
      dispatch(showSuccessSnackbar("error", "Please select at least one lead to call.", 3000));
      return { success: false, error: "No leads selected" };
    }

    try {
      const adminBaseURL = getAdminURL(getState());
      const { data } = await axios({
        method: "POST",
        url: `${process.env.REACT_APP_HOST}${adminBaseURL}/coldLeadsCalling/bulkCall`,
        headers: {
          "Content-Type": "application/json",
        },
        data: {
          leadIds,
        },
      });

      if (data?.code === 200 && data?.status === true) {
        dispatch(
          showSuccessSnackbar(
            "success",
            data?.message || "Bulk calls initiated successfully",
            5000
          )
        );
        return { success: true, data: data.data };
      } else {
        dispatch(
          showSuccessSnackbar(
            "error",
            data?.message || "Failed to initiate bulk calls",
            3000
          )
        );
        return { success: false, error: data?.message || "Failed to initiate bulk calls" };
      }
    } catch (error) {
      console.error("Failed to initiate bulk calls", error);
      const errorMessage =
        error?.response?.data?.message ||
        error?.message ||
        "Error while initiating bulk calls";
      dispatch(showSuccessSnackbar("error", errorMessage, 3000));
      return { success: false, error: errorMessage };
    }
  };
};

export const getBatchNamesAsync = () => {
  return async (dispatch, getState) => {
    try {
      const adminBaseURL = getAdminURL(getState());
      const { user } = getState().auth;
      const createdByFilter = getState().coldLeads?.createdByFilter ?? "";

      let url = `${process.env.REACT_APP_HOST}${adminBaseURL}/coldLeadsCalling/getBatchNames`;

      const queryParts = [];
      // eslint-disable-next-line eqeqeq
      const isElevated =
        user &&
        (user.roleId == 1 ||
          String(user.email || "")
            .trim()
            .toLowerCase() === "himanshu.soni@webcluesinfotech.com");

      // Sales: only their batches
      // eslint-disable-next-line eqeqeq
      if (user && user._id && user.roleId != 1 && user.email != "himanshu.soni@webcluesinfotech.com") {
        queryParts.push(
          `created_by=${encodeURIComponent(normalizeCreatedByQueryValue(user._id))}`
        );
      } else if (isElevated) {
        const v = normalizeCreatedByQueryValue(createdByFilter);
        queryParts.push(
          `created_by=${encodeURIComponent(v || "all")}`
        );
      }

      if (queryParts.length) {
        url += `?${queryParts.join("&")}`;
      }

      const { data } = await axios({
        method: "GET",
        url,
        headers: {
          "Content-Type": "application/json",
        },
      });
      
      if (data?.code === 200) {
        // Handle response structure: { data: { batchNames: [...], total: number } }
        let batchData = [];
        
        if (data.data) {
          // Structure 1: { data: { batchNames: [...] } } - Most common
          if (data.data.batchNames && Array.isArray(data.data.batchNames)) {
            batchData = data.data.batchNames;
          }
          // Structure 2: { data: [...] } - data.data is the array itself
          else if (Array.isArray(data.data)) {
            batchData = data.data;
          }
          // Structure 3: If it's an object, try to extract array values
          else if (typeof data.data === 'object') {
            batchData = Object.values(data.data);
            // Filter to ensure we only have strings
            batchData = batchData.filter(item => typeof item === 'string');
          }
        }
        
        // Ensure we always return an array
        if (!Array.isArray(batchData)) {
          batchData = [];
        }
        
        return { success: true, data: batchData };
      } else {
        return { success: false, error: data?.message || "Failed to fetch batch names", data: [] };
      }
    } catch (error) {
      console.error("Failed to fetch batch names", error);
      return { success: false, error: error?.message || "Error while fetching batch names", data: [] };
    }
  };
};

export const getCreatedByUsersAsync = () => {
  return async (dispatch, getState) => {
    try {
      const adminBaseURL = getAdminURL(getState());
      const url = `${process.env.REACT_APP_HOST}${adminBaseURL}/coldLeadsCalling/getCreatedByUsers`;
      const { data } = await axios({
        method: "GET",
        url,
        headers: {
          "Content-Type": "application/json",
        },
      });

      if (data?.code === 200) {
        const users = Array.isArray(data.data) ? data.data : [];
        return { success: true, data: users };
      }
      return { success: false, error: data?.message || "Failed to fetch created by users", data: [] };
    } catch (error) {
      console.error("Failed to fetch created by users", error);
      return { success: false, error: error?.message || "Error while fetching created by users", data: [] };
    }
  };
};

export const downloadSampleExcelAsync = () => {
  return async (dispatch, getState) => {
    try {
      const adminBaseURL = getAdminURL(getState());
      const response = await axios({
        method: "GET",
        url: `${process.env.REACT_APP_HOST}${adminBaseURL}/coldLeadsCalling/downloadSample`,
        responseType: "blob",
        headers: {
          "Content-Type": "application/json",
        },
      });

      // Create a blob from the response
      const blob = new Blob([response.data], {
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      });

      // Create a temporary URL and trigger download
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", "cold_leads_sample.xlsx");
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);

      dispatch(showSuccessSnackbar("success", "Sample file downloaded successfully", 3000));
      return { success: true };
    } catch (error) {
      console.error("Failed to download sample file", error);
      const errorMessage =
        error?.response?.data?.message ||
        error?.message ||
        "Error while downloading sample file";
      dispatch(showSuccessSnackbar("error", errorMessage, 3000));
      return { success: false, error: errorMessage };
    }
  };
};

// Update cold lead - Backend handles permission check:
// Super Admin (roleId == 1): Can update any lead
// Sales Team: Can only update their own leads (created_by = req._id)
export const updateColdLeadAsync = (leadId, updateData) => {
  return async (dispatch, getState) => {
    if (!leadId) {
      dispatch(showSuccessSnackbar("error", "Lead ID is required to update.", 3000));
      return { success: false, error: "Lead ID is required" };
    }

    try {
      const adminBaseURL = getAdminURL(getState());
      const { data } = await axios({
        method: "PUT",
        url: `${process.env.REACT_APP_HOST}${adminBaseURL}/coldLeadsCalling/update/${leadId}`,
        headers: {
          "Content-Type": "application/json",
        },
        data: updateData,
      });

      if (data?.code === 200 && data?.status === true) {
        dispatch(
          showSuccessSnackbar(
            "success",
            data?.message || "Lead updated successfully",
            3000
          )
        );
        // Update the selected cold lead in Redux state
        if (data.data) {
          dispatch(coldLeadsAction.getColdLeadSuccess(data.data));
        }
        return { success: true, data: data.data };
      } else {
        // Check if it's a duplicate contact number error
        const isDuplicate = data?.message?.toLowerCase().includes("already exists") || 
                           data?.message?.toLowerCase().includes("duplicate");
        dispatch(
          showSuccessSnackbar(
            "error",
            data?.message || "Failed to update lead",
            3000
          )
        );
        return { 
          success: false, 
          error: data?.message || "Failed to update lead",
          isDuplicate 
        };
      }
    } catch (error) {
      console.error("Failed to update cold lead", error);
      const errorMessage =
        error?.response?.data?.message ||
        error?.message ||
        "Error while updating cold lead";
      const isDuplicate = errorMessage?.toLowerCase().includes("already exists") || 
                         errorMessage?.toLowerCase().includes("duplicate");
      dispatch(showSuccessSnackbar("error", errorMessage, 3000));
      return { 
        success: false, 
        error: errorMessage,
        isDuplicate 
      };
    }
  };
};

// Update leadType - Backend handles permission check:
// Super Admin (roleId == 1): Can update any lead
// Sales Team: Can only update their own leads (created_by = req._id)
export const updateLeadTypeAsync = (leadId, leadType) => {
  return async (dispatch, getState) => {
    if (!leadId) {
      dispatch(showSuccessSnackbar("error", "Lead ID is required to update lead type.", 3000));
      return { success: false, error: "Lead ID is required" };
    }

    try {
      const adminBaseURL = getAdminURL(getState());
      const { data } = await axios({
        method: "PUT",
        url: `${process.env.REACT_APP_HOST}${adminBaseURL}/coldLeadsCalling/updateLeadType/${leadId}`,
        headers: {
          "Content-Type": "application/json",
        },
        data: { leadType },
      });

      if (data?.code === 200 && data?.status === true) {
        dispatch(
          showSuccessSnackbar(
            "success",
            data?.message || "Lead type updated successfully",
            3000
          )
        );
        return { success: true, data: data.data };
      } else {
        dispatch(
          showSuccessSnackbar(
            "error",
            data?.message || "Failed to update lead type",
            3000
          )
        );
        return { 
          success: false, 
          error: data?.message || "Failed to update lead type"
        };
      }
    } catch (error) {
      console.error("Failed to update lead type", error);
      const errorMessage =
        error?.response?.data?.message ||
        error?.message ||
        "Error while updating lead type";
      dispatch(showSuccessSnackbar("error", errorMessage, 3000));
      return { 
        success: false, 
        error: errorMessage
      };
    }
  };
};

// Check if contact number already exists
// Backend handles permission check:
// Super Admin (roleId == 1): Can check any contact number
// Sales Team: Can only check contact numbers for their own leads
export const checkContactNumberExistsAsync = (contactNumber, excludeId = null) => {
  return async (dispatch, getState) => {
    if (!contactNumber || contactNumber.trim() === "") {
      return { success: true, exists: false };
    }

    try {
      const adminBaseURL = getAdminURL(getState());
      let url = `${process.env.REACT_APP_HOST}${adminBaseURL}/coldLeadsCalling/checkContactNumber`;
      
      // Add excludeId as query parameter if provided
      if (excludeId) {
        url += `?excludeId=${excludeId}`;
      }

      const { data } = await axios({
        method: "POST",
        url,
        headers: {
          "Content-Type": "application/json",
        },
        data: { contactNumber: contactNumber.trim() },
      });

      if (data?.code === 200) {
        const exists = data.data?.exists || false;
        return { 
          success: true, 
          exists,
          data: data.data 
        };
      } else {
        return { 
          success: false, 
          exists: false,
          error: data?.message || "Failed to check contact number" 
        };
      }
    } catch (error) {
      console.error("Failed to check contact number", error);
      const errorMessage =
        error?.response?.data?.message ||
        error?.message ||
        "Error while checking contact number";
      return { 
        success: false, 
        exists: false,
        error: errorMessage 
      };
    }
  };
};


